// Buffer size configuration for different channel types
const CHAT_CHANNEL_BUFFER = 64 * 1024;   // 64KB for chat control messages
// QUALITY-ADAPTIVE FILE BUFFER: Sized for throughput × ~2s target latency.
// Prevents SCTP bufferbloat that starves ICE keepalives on the shared UDP transport
// while preserving maximum transfer speed for the current network quality.
// Previous fixed 40MB caused 7.5s+ bufferbloat at typical WiFi speeds → false disconnects.
const FILE_CHANNEL_BUFFER_DEFAULT = 16 * 1024 * 1024; // 16MB default (excellent)
export const FILE_CHANNEL_BUFFER_TIERS = {
  excellent: 16 * 1024 * 1024, // 16MB — ~2s at 8MB/s, ~4000 symbols headroom
  good:     12 * 1024 * 1024, // 12MB — ~3s at 4MB/s
  fair:      8 * 1024 * 1024, //  8MB — generous for fair connections
  poor:      4 * 1024 * 1024, //  4MB — stability priority, still ~1000 symbols
  unknown:  12 * 1024 * 1024, // 12MB — safe middle ground before stats arrive
} as const;
// A/V SIGNALING: 1MB buffer for SDP offers/answers and call control messages.
// FIX 7a: This channel carries signaling only (not real-time media, which uses
// native RTP transport). 1MB is generous for SDP + call control traffic.
const AV_CHANNEL_BUFFER = 1 * 1024 * 1024; // 1MB for A/V signaling

import { EventEmitter } from "events";

export class MobyChannel extends EventEmitter<{
  open: [];
  close: [];
  error: [RTCErrorEvent];
  message: [Uint8Array];
  bufferedamountlow: [];
}> {
  init?: RTCDataChannelInit;
  label: string;
  closed: boolean = false;
  channel: RTCDataChannel | null = null;
  ready: PromiseWithResolvers<void> | null = null;
  backpressure: PromiseWithResolvers<void> | null = null;
  private currentBufferThreshold: number;

  constructor(
    connection: RTCPeerConnection,
    label: string,
    init?: RTCDataChannelInit,
  ) {
    super();

    this.init = init;
    this.label = label;
    this.currentBufferThreshold =
      label === 'file' ? FILE_CHANNEL_BUFFER_DEFAULT :
      label === 'av' ? AV_CHANNEL_BUFFER :
      CHAT_CHANNEL_BUFFER;

    this.setupChannel(connection.createDataChannel(label, init));

    connection.addEventListener("datachannel", (event) => {
      if (event.channel.label !== label) {
        return;
      }
      if (
        init?.ordered !== undefined &&
        init.ordered !== event.channel.ordered
      ) {
        return;
      }
      if (
        init?.maxPacketLifeTime !== undefined &&
        init.maxPacketLifeTime !== event.channel.maxPacketLifeTime
      ) {
        return;
      }
      if (
        init?.maxRetransmits !== undefined &&
        init.maxRetransmits !== event.channel.maxRetransmits
      ) {
        return;
      }
      if (
        init?.protocol !== undefined &&
        init.protocol !== event.channel.protocol
      ) {
        return;
      }

      this.setupChannel(event.channel);
    });
  }

  close() {
    this.closed = true;
    this.channel?.close();
  }

  /**
   * P0-P: Unwind any send() calls suspended on `.ready` or `.backpressure`
   * without requiring the RTCDataChannel to fire a 'close' event.  Used by
   * peer-recovery paths that detach this MobyChannel instance via
   * `disposeTimersAndWorkers() + connection.close()` — those paths do NOT
   * call this.close(), so `this.closed` stays false and the 'close' event
   * handler's `if (this.closed)` guard skips the reject.  Suspended send()s
   * would otherwise retain their chunk buffers in closure forever, each
   * rooting ~32 MB (chunk) plus the whole MobyChannel via captured `this`.
   * Observed in 4/23 13:22 heap dominator: 119+ of these chains × ~176 MB
   * each = 21 GB of pinned memory on the sender's main thread.
   */
  cancelPending(reason: string): void {
    if (this.ready !== null) {
      this.ready.reject(new Error(reason));
      this.ready = null;
    }
    // Resolve (don't reject) backpressure so send() re-checks channel state
    // at line 194 and throws naturally — matches the existing close-event
    // path behavior and FIX 10A semantics.
    if (this.backpressure !== null) {
      this.backpressure.resolve();
      this.backpressure = null;
    }
  }

  /**
   * Dynamically adjust the buffer threshold based on network quality.
   * Called by provider when connection quality changes (excellent → good → fair → poor).
   * Updates both the internal backpressure threshold and the live channel's
   * bufferedAmountLowThreshold so the bufferedamountlow event fires at the right level.
   */
  setBufferThreshold(bytes: number): void {
    this.currentBufferThreshold = bytes;
    if (this.channel && this.channel.readyState === 'open') {
      this.channel.bufferedAmountLowThreshold = bytes * 0.75;
    }
  }

  getBufferThreshold(): number {
    return this.currentBufferThreshold;
  }

  setupChannel(channel: RTCDataChannel) {
    channel.binaryType = "arraybuffer";
    
    // Align threshold with conservativeThreshold (75%) used in send()
    // Prevents race where event fires at 100% but send() waits for 75%
    channel.bufferedAmountLowThreshold = this.currentBufferThreshold * 0.75;

    channel.addEventListener("open", () => {
      this.channel = channel;
      if (this.ready !== null) {
        this.ready.resolve();
        this.ready = null;
      }

      this.emit("open");
    });

    channel.addEventListener("close", () => {
      this.channel = null;

      // CRITICAL FIX 10A: Resolve backpressure to unblock any pending send() calls.
      // Without this, send() hangs forever when the channel closes during backpressure
      // wait — bufferedamountlow never fires on a closed channel, so the promise
      // never resolves. Each hung send() retains its caller's scope (~31MB of
      // chunk + symbols), causing unbounded memory growth (observed: 5.4GB).
      if (this.backpressure !== null) {
        this.backpressure.resolve();
        this.backpressure = null;
      }

      this.emit("close");

      if (this.closed) {
        if (this.ready !== null) {
          this.ready.reject(new Error("Channel closed"));
          this.ready = null;
        }
      }
    });

    channel.addEventListener("error", (event) => {
      this.emit("error", event);
    });

    channel.addEventListener("bufferedamountlow", () => {
      if (this.backpressure !== null) {
        this.backpressure.resolve();
        this.backpressure = null;
      }

      this.emit("bufferedamountlow");
    });

    channel.addEventListener("message", (event) => {
      if (!(event.data instanceof ArrayBuffer)) {
        return;
      }

      this.emit("message", new Uint8Array(event.data));
    });
  }

  async send(bytes: Uint8Array) {
    while (this.channel === null || this.channel.readyState !== "open") {
      if (this.ready === null) {
        this.ready = Promise.withResolvers<void>();
      }
      await this.ready.promise;
    }

    // Use instance-level threshold (dynamically adjusted by provider based on network quality)
    // 75% conservative threshold prevents one peer monopolizing during multi-peer scenarios
    const conservativeThreshold = this.currentBufferThreshold * 0.75;
    
    while (this.channel !== null && this.channel.bufferedAmount > conservativeThreshold) {
      if (this.backpressure === null) {
        this.backpressure = Promise.withResolvers<void>();
      }
      // FIX 10B: Safety timeout prevents indefinite blocking if bufferedamountlow
      // never fires (e.g., channel in limbo, browser bug, OS sleep).
      // 30s is generous — normal backpressure resolves in <1s at typical bandwidth.
      // P0-P: Clear the setTimeout the instant backpressure wins the race so
      // the timer's mCallback stops retaining the send() activation (which
      // captures this MobyChannel + the `bytes` chunk — ~32 MB each).
      let timerId: ReturnType<typeof setTimeout> | null = null;
      const timeout = new Promise<void>((resolve) => {
        timerId = setTimeout(resolve, 30_000);
      });
      try {
        await Promise.race([this.backpressure.promise, timeout]);
      } finally {
        if (timerId !== null) clearTimeout(timerId);
      }
    }

    // FIX 10A: After backpressure wait, channel may have closed — re-check
    if (this.channel === null || this.channel.readyState !== "open") {
      throw new Error("Channel closed during backpressure wait");
    }

    this.channel.send(bytes as ArrayBufferView<ArrayBuffer>);
  }
}
